/* eslint-disable @sailor/i18n-no-country-codes */
import React, { useState, useMemo } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { fetchReceiptDownload } from '../../utils/api'
import { failToast } from '@/common/utils/toast'
import { downloadFile } from '@/common/utils'
import DropMenu from '@roo/roo/DropMenu'
import Button from '@roo/roo/Button'
import Icon from '@roo/roo/Icon'
import { getLanguage, languageOptions } from '@/common/utils/i18n'
import { OrderDownloadType } from '@/common/utils/consts'
import { BizOrderStatusEnum, PayTypeEnum } from '../../types'

interface ReceiptDownloadProps {
  bizId: string;
  bizOrderStatus: number;
  payType?: PayTypeEnum;
}

// 下拉菜单宽度配置 - 按语言区分
const DROPDOWN_WIDTH_MAP = {
  en: 102,
  'zh-HK': 102,
  // ar: 142,
} as const

// 获取下拉菜单宽度的辅助函数
const getDropdownWidth = () => {
  const lang = getLanguage()
  return DROPDOWN_WIDTH_MAP[lang as keyof typeof DROPDOWN_WIDTH_MAP] ?? DROPDOWN_WIDTH_MAP['zh-HK']
}

const ReceiptDownload: React.FC<ReceiptDownloadProps> = ({ bizId, bizOrderStatus, payType }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  /*
   * 判断是否应该置灰
   * 置灰场景：已提单(10)、已支付(20)、商家已确认(30)、已取消(50)+payType为空
   */
  const isDisabled = useMemo(() => {
    if (bizOrderStatus === BizOrderStatusEnum.Submitted ||
        bizOrderStatus === BizOrderStatusEnum.Paid ||
        bizOrderStatus === BizOrderStatusEnum.Confirmed) {
      return true
    }
    if (bizOrderStatus === BizOrderStatusEnum.Cancelled && !payType) {
      return true
    }
    // 个人支付不支持下载，仅支付企业支付和组合支付
    if (payType === PayTypeEnum.PersonalPay) {
      return true
    }
    return false
  }, [bizOrderStatus, payType])

  const handleDownloadReceipt = async (locale: string) => {
    if (isLoading || isDisabled) return

    try {
      setIsLoading(true)
      const { url } = await fetchReceiptDownload({ bizId, locale, type: OrderDownloadType.ORDER_RECEIPT })
      // 使用公共方法下载文件
      downloadFile({ url: url })
    } catch (error) {
      console.error('Download receipt error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // 定义下拉菜单内容
  const menu = languageOptions.map(option => ({
    label: option.label,
    onClick: () => handleDownloadReceipt(option.value),
  }))

  return (
    <DropMenu
      menu={menu}
      disabled={isLoading || isDisabled}
      style={{ width: getDropdownWidth() }}
      trigger="click"
      placement="bottomLeft"
    >
      <Button
        type="brand-text"
        disabled={isLoading || isDisabled}
        loading={isLoading}
        style={{ borderRadius: 6 }}
      >
        {t('e_pc_download_receipt', '下载收据')}
      </Button>
    </DropMenu>
  )
}

export default ReceiptDownload